import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LandingHttpService } from '@observation/services/landing-http.service';
import { API_URL, APP_CONFIG, ConfigSettings } from '@shared/config';
import { SubjectArea } from '@shared/models';
import { ApiResponse } from '@shared/models/api-response.model';
import { DashboardInfoModel } from '@shared/models/dashboard.model';
import { Dimension } from '@shared/models/dimension.model';
import { LandingAreasMenu } from '@shared/models/landing.model';
import { IsValidSubjectArea } from '@shared/models/state/subject-area.model';
import { DataApiService, LayoutService } from '@shared/services';
import { GlobalFilterService } from '@shared/services/global-filter.service';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  forkJoin,
  of,
} from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import { HttpErrorSkip } from 'src/app/interceptors/http-error/http-error-skip.model';

@Injectable()
export class SubjectAreaService {
  globalSettings: ConfigSettings;
  activeSubjectArea: SubjectArea;
  dimensionNames: { name: string; id: string }[];
  metricNames: { name: string; id: string }[];

  private activeSubjectAreaSubject = new BehaviorSubject<SubjectArea | null>(
    null
  );
  public activeSubjectArea$ = this.activeSubjectAreaSubject.asObservable();

  private errorResolver = new ReplaySubject<boolean>(1);
  public errorResolver$ = this.errorResolver.asObservable();

  private subjectAreaCouldNotBeLoaded = new Subject<void>();
  public subjectAreaCouldNotBeLoaded$ =
    this.subjectAreaCouldNotBeLoaded.asObservable();

  private hasSubjectAreaFilters = new BehaviorSubject<boolean>(true);
  private hasCustomVisualFilters = new BehaviorSubject<boolean>(true);

  public hasSubjectAreaFilters$ = this.hasSubjectAreaFilters.asObservable();
  public hasCustomVisualFilters$ = this.hasCustomVisualFilters.asObservable();

  private readonly subjectAreasMenuItems: BehaviorSubject<LandingAreasMenu[]> =
    new BehaviorSubject<LandingAreasMenu[]>([]);
  public subjectAreasMenuItems$ = this.subjectAreasMenuItems.asObservable();

  constructor(
    @Inject(APP_CONFIG) settings: ConfigSettings,
    private readonly http: HttpClient,
    private readonly layoutService: LayoutService,
    private readonly globalFilterService: GlobalFilterService,
    private readonly dataApiService: DataApiService,
    private readonly landingHttpService: LandingHttpService
  ) {
    this.globalSettings = settings;
    this.initializeSubjectAreaMenuItems();
  }

  setErrorResolver(error: boolean) {
    this.errorResolver.next(error);
  }

  public changeSubjectAreaCouldNotBeLoaded() {
    this.subjectAreaCouldNotBeLoaded.next();
  }

  getSubjectAreaById(id: number, skip404: boolean): Observable<SubjectArea> {
    const url = `${this.globalSettings.orApiUrl}${API_URL.subjectArea}/findWithNeededData/${id}`;
    if (!skip404) {
      return this.http.get<SubjectArea>(url, { withCredentials: true });
    }
    return this.http.get<SubjectArea>(url, {
      withCredentials: true,
      headers: {
        [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
          skipStatusCodes: [404],
        }).parseToString(),
      },
    });
  }

  isValidSubjectArea(
    id: number,
    skip404: boolean
  ): Observable<IsValidSubjectArea> {
    const url = `${this.globalSettings.orApiUrl}${API_URL.subjectArea}/${id}/isValid`;
    if (!skip404) {
      return this.http.get<IsValidSubjectArea>(url, { withCredentials: true });
    }
    return this.http.get<IsValidSubjectArea>(url, {
      withCredentials: true,
      headers: {
        [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
          skipStatusCodes: [404],
        }).parseToString(),
      },
    });
  }

  getFirstLayoutFromSubjectAreaId(id: number): Observable<DashboardInfoModel> {
    const url = `${this.globalSettings.orApiUrl}${API_URL.subjectArea}/${id}/firstDashboard`;
    return this.http.get<DashboardInfoModel>(url, {
      withCredentials: true,
    });
  }

  changeSubjectAreas(subjectArea: SubjectArea): void {
    this.activeSubjectAreaSubject.next(subjectArea);
    const dimensionNames$ = this.dataApiService.getDimensionsNames(
      subjectArea.dataSourceId
    );

    const metricNames$ = this.dataApiService
      .getMetricsNames(subjectArea.dataSourceId)
      .pipe(catchError(err => of({ err })));

    forkJoin([dimensionNames$, metricNames$]).subscribe(
      ([dimensionNames, metricNames]) => {
        this.dimensionNames = !dimensionNames.hasOwnProperty('err')
          ? dimensionNames.data
          : [];
        this.metricNames = !metricNames.hasOwnProperty('err')
          ? (<ApiResponse<Dimension[]>>metricNames).data
          : [];
        const subjectAreaFilters = subjectArea.subjectAreaFilters;

        const hasFilters =
          subjectAreaFilters?.length > 0 ||
          subjectArea.layoutFilters.length > 0;

        this.hasSubjectAreaFilters.next(hasFilters);
        this.checkHasCustomVisualFilters(subjectArea).subscribe(value =>
          this.hasCustomVisualFilters.next(value)
        );

        this.layoutService.changeLayouts(subjectArea.layouts);
        this.globalFilterService.setSubjectAreaFilters(subjectAreaFilters);
      }
    );
  }

  checkHasCustomVisualFilters(subjectArea: SubjectArea): Observable<boolean> {
    const url =
      `${this.globalSettings.orApiUrl}${API_URL.subjectArea}` +
      `/hasCustomVisualFilters?subjectAreaId=${subjectArea.subjectAreaId}`;
    return this.http.get<boolean>(url, { withCredentials: true });
  }

  resetSubjectArea(): void {
    this.activeSubjectAreaSubject.next(null);
  }

  public initializeSubjectAreaMenuItems(): void {
    this.landingHttpService
      .getAreaMenu()
      .pipe(
        take(1),
        map(data => [...data].sort((a, b) => a.position - b.position)),
        filter(landings => landings.length > 0),
        map(landings =>
          landings.map(landing => {
            landing.landingItems = [...landing.landingItems].sort((a, b) =>
              a.landingItemName.localeCompare(b.landingItemName)
            );
            return landing;
          })
        ),
        catchError(() => of([]))
      )
      .subscribe(landings => this.subjectAreasMenuItems.next(landings));
  }
}
